import { createSlice } from "@reduxjs/toolkit";
import {
  BatchAttendanceRecord,
  StudentAttendance,
} from "@src/api/TPAttendanceApiSlice";
import { formatDate } from "@src/pages/batches/attendance/home/components/sidebar/typesUtils";
import { Batch } from "@src/pages/batches/batch-base-component/data.type";

const defaultAttendanceState = {
  attendanceCount: {
    perfect: 0,
    compliant: 0,
    atRisk: 0,
    defaulter: 0,
  },
  attendanceStatusActiveFilter: null,
  selectedDateAttendance: null,
  selectedDateReport: null,
  recordsByDate: [],
  selectedSession: "",
  recordsByDateSidebar: [],
  sessionListSidebar: null,
  selectedSessionSidebar: "",
  studentData: [],
  weeklyData: { average: 0, daysMarked: 0, totalDays: 0 },
  weeklyReport: [],
  attendanceMarking: null,
  sessionList: null,
  submittedSessions: {},
  editingSessions: {},
  attendanceLastUpdatedAt: null,
  attendanceLastUpdatedDate: null,
};

type InitialState = {
  batch: Batch | null;
  attendanceCount: {
    perfect: number;
    compliant: number;
    atRisk: number;
    defaulter: number;
  };
  attendanceStatusActiveFilter: number | null;
  selectedDateAttendance: Date | null | string;
  selectedDateReport: Date | string | null;
  recordsByDate: BatchAttendanceRecord[];
  recordsByDateSidebar: BatchAttendanceRecord[];
  studentData: StudentAttendance[];
  weeklyData: Record<"average" | "daysMarked" | "totalDays", string | number>;
  weeklyReport: any;
  attendanceMarking: any;
  sessionListSidebar: any;
  selectedSessionSidebar: string;
  selectedSession: string;
  sessionList: any;
  submittedSessions: Record<string, boolean>;
  editingSessions: Record<string, boolean>;
  attendanceLastUpdatedAt: string | null;
  attendanceLastUpdatedDate: string | null;
};

const initialState: InitialState = {
  batch: null,
  ...defaultAttendanceState,
};

const attendanceSlice = createSlice({
  name: "attendance",
  initialState: initialState,
  reducers: {
    setSelectedBatchForAttendance: (state, action) => {
      state.batch = action.payload;
      Object.assign(state, defaultAttendanceState);
    },
    setAttendanceCount: (state, action) => {
      state.attendanceCount = action.payload;
    },
    setAttendanceStatusActiveFilter: (state, action) => {
      state.attendanceStatusActiveFilter = action.payload;
    },
    setSelectedDateAttendance: (state, action) => {
      state.selectedDateAttendance = action?.payload;
    },
    setSelectedDateReport: (state, action) => {
      state.selectedDateReport = action?.payload;
    },
    setRecordsByDate: (state, action) => {
      const records: BatchAttendanceRecord[] = action.payload ?? [];

      const tempSessionList = {
        name: "Session 1",
        id: "123",
        classSessionStatus: "",
        startTime: "",
        endTime: "",
        date: formatDate(new Date()),
      };

      state.recordsByDate = records;

      state.selectedSession =
        records.length > 0 ? (records[0]?.id ?? "") : "123";

      state.sessionList =
        records.length > 0
          ? records.map(({ studentAttendance, ...rest }) => rest)
          : [tempSessionList];

      if (!state.attendanceMarking) {
        state.attendanceMarking = {};
      }

      records.forEach((session: any) => {
        const sessionId = session.id;
        const sessionStatus = session.classSessionStatus;
        const studentAttendance = session.studentAttendance ?? {};
        const hasAttendanceFromAPI = Object.keys(studentAttendance).length > 0;

        // -----------------------------
        // CASE 1: COMPLETED
        // -----------------------------
        if (sessionStatus === "COMPLETED") {
          const mapped: Record<string, { status: string; isEdited: boolean }> =
            {};

          Object.entries(studentAttendance).forEach(([studentId, status]) => {
            mapped[studentId] = {
              status: status as string,
              isEdited: false,
            };
          });

          state.attendanceMarking[sessionId] = mapped;
          return;
        }

        // -----------------------------
        // CASE 2: INPROGRESS + API data
        // -----------------------------
        if (sessionStatus === "INPROGRESS" && hasAttendanceFromAPI) {
          const mapped: Record<string, { status: string; isEdited: boolean }> =
            {};

          Object.entries(studentAttendance).forEach(([studentId, status]) => {
            mapped[studentId] = {
              status: status as string,
              isEdited: false,
            };
          });

          state.attendanceMarking[sessionId] = mapped;
          return;
        }

        // -----------------------------
        // CASE 3: INPROGRESS + no API data
        // -----------------------------
        if (sessionStatus === "INPROGRESS") {
          // If already present (user edited), DO NOT override
          if (state.attendanceMarking[sessionId]) {
            return;
          }

          // Initialize from studentData
          const mapped: Record<string, { status: string; isEdited: boolean }> =
            {};

          state.studentData?.forEach((student) => {
            mapped[student.id] = {
              status: "",
              isEdited: false,
            };
          });

          state.attendanceMarking[sessionId] = mapped;
        }
      });
    },

    setAddSessionList: (state) => {
      const currentList = state.sessionList ?? [];

      const nextNumber = currentList.length + 1;

      const newSession = {
        name: `Session ${nextNumber}`,
        id: Math.floor(100 + Math.random() * 900).toString(),
        classSessionStatus: "",
        startTime: "",
        endTime: "",
        date: formatDate(new Date()),
      };

      state.sessionList = [...currentList, newSession];
      state.selectedSession = newSession.id; // optional: auto-select new session
    },
    replaceActiveSession: (state, action) => {
      const updatedSession = action.payload;
      const currentList = state.sessionList ?? [];

      const activeIndex = currentList.findIndex(
        (s: any) => s.id === state.selectedSession,
      );

      if (activeIndex !== -1) {
        currentList[activeIndex] = updatedSession;
        state.sessionList = [...currentList]; // ensure immutability
        state.selectedSession = updatedSession.id; // re-affirm selection
      }
    },
    setSelectedSession: (state, action) => {
      state.selectedSession = action.payload;
    },
    setStudentData: (state, action) => {
      state.studentData = action?.payload;
    },
    setWeeklyData: (state, action) => {
      const {
        presentAttendancePercentage,
        daysMarked,
        totalDays,
        sessionResponseList,
      } = action.payload;
      state.weeklyData = {
        average: presentAttendancePercentage,
        daysMarked,
        totalDays,
      };
      state.weeklyReport = sessionResponseList ?? [];
    },
    setRecordsByDateSidebar: (state, action) => {
      const records: BatchAttendanceRecord[] = action.payload ?? [];
      state.recordsByDateSidebar = records;
      state.selectedSessionSidebar = records[0]?.id ?? "";

      state.sessionListSidebar = records.map(
        ({ studentAttendance, ...rest }) => rest,
      );
    },
    setSelectedSessionSidebar: (state, action) => {
      state.selectedSessionSidebar = action?.payload;
    },
    updateStudentAttendance: (state, action) => {
      const { sessionId, studentId, status } = action.payload;

      if (!state.attendanceMarking) return;

      if (!state.attendanceMarking[sessionId]) {
        state.attendanceMarking[sessionId] = {};
      }

      state.attendanceMarking[sessionId][studentId] = {
        status,
        isEdited: false,
      };
    },
    markAllAttendance: (state, action) => {
      const { sessionId, status } = action.payload;

      if (!sessionId || !state.studentData?.length) return;

      const sessionMap: Record<
        string,
        { status: "PRESENT" | "ABSENT"; isEdited: boolean }
      > = {};

      state.studentData.forEach((student) => {
        sessionMap[student.id] = {
          status,
          isEdited: false,
        };
      });

      if (!state.attendanceMarking) {
        state.attendanceMarking = {};
      }

      state.attendanceMarking[sessionId] = sessionMap;
    },
    markSessionSubmitted: (state, action) => {
      const { sessionId } = action.payload;
      if (!state.submittedSessions) {
        state.submittedSessions = {};
      }
      state.submittedSessions[sessionId] = true;
    },
    enableSessionEdit: (state, action) => {
      const { sessionId } = action.payload;

      if (!state.editingSessions) {
        state.editingSessions = {};
      }

      state.editingSessions[sessionId] = true;
    },
    disableSessionEdit: (state, action) => {
      const { sessionId } = action.payload;

      if (!state.editingSessions) return;

      delete state.editingSessions[sessionId];
    },
    setAttendanceUpdated: (state, action) => {
      const { date } = action.payload;

      state.attendanceLastUpdatedAt = new Date().toISOString();
      state.attendanceLastUpdatedDate = date;
    },

    setAttendanceUpdatedClear: (state) => {
      state.attendanceLastUpdatedAt = null;
      state.attendanceLastUpdatedDate = null;
    },
    updateTrainerAttendanceStatus: (state, action) => {
      const { sessionId, status } = action.payload;
      if (state.recordsByDate) {
        const record = state.recordsByDate.find((r) => r.id === sessionId);
        if (record) {
          record.trainerAttendance = status;
        }
      }
      if (state.sessionList) {
        const session = state.sessionList.find((s: any) => s.id === sessionId);
        if (session) {
          session.trainerAttendance = status;
        }
      }
    },
  },
});

export const {
  setSelectedBatchForAttendance,
  setAttendanceCount,
  setAttendanceStatusActiveFilter,
  setSelectedDateAttendance,
  setSelectedDateReport,
  setRecordsByDate,
  setStudentData,
  setRecordsByDateSidebar,
  setWeeklyData,
  setSelectedSessionSidebar,
  setAddSessionList,
  setSelectedSession,
  replaceActiveSession,
  updateStudentAttendance,
  markAllAttendance,
  markSessionSubmitted,
  enableSessionEdit,
  disableSessionEdit,
  setAttendanceUpdated,
  setAttendanceUpdatedClear,
  updateTrainerAttendanceStatus,
} = attendanceSlice.actions;

export default attendanceSlice.reducer;
